import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ReassuranceSection from '@/components/feature/ReassuranceSection';

const FORM_URL = 'https://formspree.io/f/meewdjbl';

export default function ContactoPage() {
  const location = useLocation();
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    asunto: '',
    mensaje: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.mensaje.length > 500) {
      setError('El mensaje no puede superar los 500 caracteres.');
      return;
    }

    setLoading(true);
    try {
      const body = new URLSearchParams({
        nombre: formData.nombre,
        email: formData.email,
        telefono: formData.telefono,
        asunto: formData.asunto,
        mensaje: formData.mensaje,
        _subject: 'Contacto — Toldos Málaga',
        formulario: 'Contacto',
      });

      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
        body,
      });
      setSubmitted(true);
    } catch (err) {
      setError('Error al enviar el mensaje. Inténtalo de nuevo o llámanos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <title>Contacto | Toldos Málaga | Reparación e Instalación de Toldos</title>
      <meta name="description" content="Contacta con Toldos Málaga en Calle Pacífico 54, San Andrés, 29003 Málaga. Teléfono 951 12 07 70, WhatsApp 627 21 33 47. Presupuesto gratis para reparación e instalación de toldos." />
      <meta name="keywords" content="contacto toldos Málaga, toldero Málaga contacto, reparación toldos Málaga teléfono, toldos Málaga dirección" />
      <link rel="canonical" href="https://toldos-malaga.es/contacto" />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://toldos-malaga.es/' },
              { '@type': 'ListItem', position: 2, name: 'Contacto', item: 'https://toldos-malaga.es/contacto' },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            mainEntity: {
              '@type': 'LocalBusiness',
              '@id': 'https://toldos-malaga.es/#business',
              name: 'Toldos Málaga',
              description: 'Reparación e instalación de toldos en Málaga. Tolderos profesionales con respuesta rápida 24/48h. Presupuesto gratis.',
              url: 'https://toldos-malaga.es',
              image: 'https://toldos-malaga.es/og-image.jpg',
              logo: 'https://toldos-malaga.es/logo.svg',
              telephone: '+34951120770',
              email: 'info@toldos-malaga.es',
              address: {
                '@type': 'PostalAddress',
                streetAddress: 'Calle Pacífico 54',
                addressLocality: 'Málaga',
                postalCode: '29003',
                addressRegion: 'Málaga',
                addressCountry: 'ES',
              },
              geo: {
                '@type': 'GeoCoordinates',
                latitude: 36.7213,
                longitude: -4.4214,
              },
              openingHoursSpecification: [
                {
                  '@type': 'OpeningHoursSpecification',
                  dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                  opens: '09:00',
                  closes: '18:00',
                },
                {
                  '@type': 'OpeningHoursSpecification',
                  dayOfWeek: 'Saturday',
                  opens: '10:00',
                  closes: '14:00',
                },
              ],
              priceRange: '€€',
              areaServed: {
                '@type': 'City',
                name: 'Málaga',
              },
            },
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+34951120770',
              contactType: 'customer service',
              email: 'info@toldos-malaga.es',
              availableLanguage: ['Spanish'],
              areaServed: 'ES',
            },
          }),
        }}
      />
      <meta property="og:title" content="Contacto | Toldos Málaga | Reparación e Instalación de Toldos" />
      <meta property="og:description" content="Contacta con Toldos Málaga en Calle Pacífico 54, San Andrés, 29003 Málaga. Teléfono 951 12 07 70, WhatsApp 627 21 33 47. Presupuesto gratis para reparación e instalación de toldos." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://toldos-malaga.es/contacto" />
      <meta property="og:image" content="https://toldos-malaga.es/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Toldos Málaga" />
      <meta property="og:locale" content="es_ES" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Contacto | Toldos Málaga" />
      <meta name="twitter:description" content="Contacta con Toldos Málaga. Tel 951 12 07 70. WhatsApp 627 21 33 47. Calle Pacífico 54, Málaga." />
      <meta name="twitter:image" content="https://toldos-malaga.es/og-image.jpg" />

      <Navbar />
      <main className="min-h-screen bg-background-50">
        <nav aria-label="Breadcrumb" className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-28 pb-2">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-500 transition-colors duration-200">Inicio</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-700 font-medium">Contacto</li>
          </ol>
        </nav>

        <ReassuranceSection />

        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <header className="mb-10">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-3">
              Contacta con <span className="text-primary-500">Toldos Málaga</span>
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed max-w-2xl">
              Estamos en el barrio de San Andrés, Málaga. Llámanos, escríbenos por WhatsApp o rellena el formulario. Te respondemos en menos de 24 horas.
            </p>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Form Column */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="bg-accent-100/80 border border-accent-300/60 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-accent-500/20 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-accent-600 text-3xl"></i>
                  </div>
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">¡Mensaje enviado!</h2>
                  <p className="text-foreground-600 mb-4">
                    Gracias por contactarnos. Te responderemos en menos de 24 horas.
                  </p>
                  <a
                    href="tel:+34951120770"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-phone-line text-lg"></i>
                    <span>¿Urgente? Llámanos: 951 12 07 70</span>
                  </a>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="contacto-toldos-malaga">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Nombre <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      required
                      value={formData.nombre}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="Tu nombre"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Correo electrónico <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="tu@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="telefono" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      id="telefono"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="600 000 000"
                    />
                  </div>
                  <div>
                    <label htmlFor="asunto" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Asunto <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="asunto"
                      name="asunto"
                      required
                      value={formData.asunto}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-background-200 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    >
                      <option value="">Selecciona un motivo</option>
                      <option value="presupuesto">Solicitar presupuesto</option>
                      <option value="reparacion">Reparación urgente</option>
                      <option value="instalacion">Consulta instalación</option>
                      <option value="hosteleria">Toldos para hostelería</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="mensaje" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Mensaje <span className="text-red-500">*</span>
                      <span className="text-foreground-400 font-normal ml-2">({formData.mensaje.length}/500)</span>
                    </label>
                    <textarea
                      id="mensaje"
                      name="mensaje"
                      required
                      maxLength={500}
                      rows={4}
                      value={formData.mensaje}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-background-200 text-foreground-900 text-sm placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      placeholder="Escribe aquí tu mensaje..."
                    />
                  </div>

                  {error && (
                    <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 whitespace-nowrap"
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line text-lg animate-spin"></i>
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <span>Enviar mensaje</span>
                        <i className="ri-send-plane-line text-lg"></i>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info + Map */}
            <div className="lg:col-span-3 space-y-6">
              {/* Map */}
              <div className="w-full h-[320px] md:h-[400px] rounded-2xl overflow-hidden">
                <iframe
                  title="Toldos Málaga - Calle Pacífico 54"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3198.5361522532425!2d-4.433233923456789!3d36.70980197234567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sCalle+Pac%C3%ADfico+54%2C+M%C3%A1laga!5e0!3m2!1ses!2ses!4v1717000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full"
                />
              </div>

              {/* Contact Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <a
                  href="tel:+34951120770"
                  className="flex flex-col items-center gap-3 p-5 rounded-2xl bg-primary-500 text-white hover:bg-primary-600 transition-all duration-300 text-center group"
                >
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <i className="ri-phone-line text-2xl"></i>
                  </div>
                  <div>
                    <span className="text-xs text-white/70 font-label">Llámanos</span>
                    <p className="text-sm font-bold">951 12 07 70</p>
                  </div>
                </a>
                <a
                  href="https://wa.me/34627213347"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-3 p-5 rounded-2xl bg-[#25D366] text-white hover:bg-[#1fb855] transition-all duration-300 text-center group"
                >
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-2xl"></i>
                  </div>
                  <div>
                    <span className="text-xs text-white/70 font-label">WhatsApp</span>
                    <p className="text-sm font-bold">627 21 33 47</p>
                  </div>
                </a>
                <a
                  href="mailto:info@toldos-malaga.es"
                  className="flex flex-col items-center gap-3 p-5 rounded-2xl bg-secondary-100 border border-secondary-200 hover:bg-secondary-200 transition-all duration-300 text-center group"
                >
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-2xl text-primary-500"></i>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500 font-label">Email</span>
                    <p className="text-sm font-bold text-foreground-900">info@toldos-malaga.es</p>
                  </div>
                </a>
              </div>

              {/* Address & Hours */}
              <div className="bg-secondary-100/80 border border-secondary-200/60 rounded-2xl p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
                      <i className="ri-map-pin-line text-primary-500"></i>
                      Dirección
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      Calle Pacífico 54<br />
                      San Andrés<br />
                      29003 Málaga
                    </p>
                    <p className="text-xs text-foreground-400 mt-2">
                      Zona de servicio: Málaga y alrededores
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
                      <i className="ri-time-line text-primary-500"></i>
                      Horario
                    </h3>
                    <ul className="space-y-1.5 text-sm text-foreground-600">
                      <li className="flex justify-between">
                        <span>Lunes a Viernes</span>
                        <span className="font-medium text-foreground-800">9:00 - 18:00</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Sábados</span>
                        <span className="font-medium text-foreground-800">10:00 - 14:00</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Domingos</span>
                        <span className="font-medium text-foreground-400">Cerrado</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}